import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Envelope, ArrowClockwise, CheckCircle, XCircle, Warning,
  Eye, ArrowSquareOut, X, Paperclip,
} from "@phosphor-icons/react";

const STATUS = {
  queued:     { cls: "bg-[#E5E2D9] text-[#5C5A55]", icon: ArrowClockwise },
  sent:       { cls: "bg-[#F9E4B7] text-[#7A5A11]", icon: Envelope },
  delivered:  { cls: "bg-[#D4E0D1] text-[#1A3317]", icon: CheckCircle },
  delayed:    { cls: "bg-[#F9E4B7] text-[#7A5A11]", icon: ArrowClockwise },
  bounced:    { cls: "bg-[#F4C7B9] text-[#7A2E18]", icon: XCircle },
  complained: { cls: "bg-[#F4C7B9] text-[#7A2E18]", icon: Warning },
  failed:     { cls: "bg-[#F4C7B9] text-[#7A2E18]", icon: XCircle },
  opened:     { cls: "bg-[#D4E0D1] text-[#1A3317]", icon: Eye },
  clicked:    { cls: "bg-[#D4E0D1] text-[#1A3317]", icon: ArrowSquareOut },
  orphan:     { cls: "bg-[#E5E2D9] text-[#5C5A55]", icon: Warning },
};

const KIND_LABEL = {
  login_credentials: "Welcome / Credentials",
  invoice: "Invoice",
  test: "Test",
  orphan: "Orphan",
  other: "Other",
};

export default function EmailLog() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [openRow, setOpenRow] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [testBusy, setTestBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (kindFilter) params.set("template_kind", kindFilter);
      const r = await api.get(`/emails${params.toString() ? `?${params}` : ""}`);
      setItems(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter, kindFilter]);

  const totals = items.reduce((acc, e) => {
    acc[e.status] = (acc[e.status] || 0) + 1;
    return acc;
  }, {});

  const sendTest = async () => {
    if (!testTo) return;
    setTestBusy(true);
    try {
      await api.post("/emails/test", { to: testTo, subject: "Rescigo Health · ping",
        message: "If you can read this, your Resend integration is live ✓" });
      toast.success(`Test email sent to ${testTo}`);
      setTestTo("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setTestBusy(false);
  };

  return (
    <AppShell title="Email Log">
      <div className="space-y-5" data-testid="email-log-page">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">Communications</div>
            <h1 className="font-heading text-2xl text-[#1A1A1A]">Email Log</h1>
            <div className="text-xs text-[#5C5A55] mt-1">
              All transactional emails sent via Resend, with real-time delivery / open / bounce events from the webhook.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)}
              placeholder="you@example.com"
              data-testid="email-test-to"
              className="h-9 w-56 px-3 border border-[#E5E2D9] rounded-md text-xs"
            />
            <button onClick={sendTest} disabled={!testTo || testBusy} data-testid="email-test-send"
                    className="h-9 px-3 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded-md text-xs font-medium">
              {testBusy ? "Sending…" : "Send test"}
            </button>
            <button onClick={load} data-testid="email-refresh"
                    className="h-9 px-3 inline-flex items-center gap-1 border border-[#E5E2D9] rounded-md text-xs hover:bg-[#F7F6F2]">
              <ArrowClockwise size={12} weight="bold" /> Refresh
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Kpi label="Sent" value={totals.sent || 0} tone="amber" />
          <Kpi label="Delivered" value={totals.delivered || 0} tone="green" />
          <Kpi label="Opened" value={totals.opened || 0} tone="green" />
          <Kpi label="Bounced" value={(totals.bounced || 0) + (totals.complained || 0)} tone="red" />
          <Kpi label="Failed" value={totals.failed || 0} tone="red" />
          <Kpi label="Total" value={items.length} tone="grey" />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="email-filters">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mr-1">Status:</div>
          {["", "sent", "delivered", "opened", "bounced", "failed"].map(s => (
            <button key={s || "all"} data-testid={`email-filter-status-${s || "all"}`}
                    onClick={() => setStatusFilter(s)}
                    className={`h-7 px-3 rounded-md text-[10px] uppercase tracking-[0.16em] border ${statusFilter === s ? "bg-[#2D5A27] border-[#2D5A27] text-white" : "bg-white border-[#E5E2D9] text-[#5C5A55]"}`}>
              {s || "All"}
            </button>
          ))}
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] ml-2 mr-1">Kind:</div>
          {["", "login_credentials", "invoice", "test"].map(k => (
            <button key={k || "all-k"} data-testid={`email-filter-kind-${k || "all"}`}
                    onClick={() => setKindFilter(k)}
                    className={`h-7 px-3 rounded-md text-[10px] uppercase tracking-[0.16em] border ${kindFilter === k ? "bg-[#2D5A27] border-[#2D5A27] text-white" : "bg-white border-[#E5E2D9] text-[#5C5A55]"}`}>
              {k ? (KIND_LABEL[k] || k) : "All"}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto">
          <table className="w-full text-sm" data-testid="email-table">
            <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
              <tr>
                <th className="px-4 py-3 text-left">Sent</th>
                <th className="px-4 py-3 text-left">Kind</th>
                <th className="px-4 py-3 text-left">To</th>
                <th className="px-4 py-3 text-left">Subject</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Events</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-6 py-12 text-center text-[#8A8780]">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="px-6 py-16 text-center text-[#8A8780]">
                  <Envelope size={28} className="inline-block mb-2 opacity-40" />
                  <div className="text-xs">No emails yet — send a test from the top bar.</div>
                </td></tr>
              )}
              {items.map((e, i) => {
                const st = STATUS[e.status] || STATUS.queued;
                const Icon = st.icon;
                return (
                  <tr key={e.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}
                      data-testid={`email-row-${e.id}`}>
                    <td className="px-4 py-3 text-xs text-[#5C5A55] whitespace-nowrap">{(e.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-3 text-xs">{KIND_LABEL[e.template_kind] || e.template_kind}</td>
                    <td className="px-4 py-3 text-xs">{(e.to || []).join(", ")}</td>
                    <td className="px-4 py-3 text-xs truncate max-w-md">
                      {e.subject}
                      {e.attachment_count > 0 && (
                        <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] text-[#5C5A55]">
                          <Paperclip size={10} /> {e.attachment_count}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-[10px] uppercase tracking-[0.18em] inline-flex items-center gap-1 ${st.cls}`}>
                        <Icon size={10} weight="bold" />{e.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#5C5A55]">{(e.events || []).length}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setOpenRow(e)} data-testid={`email-view-${e.id}`}
                              className="text-[#2D5A27] hover:underline text-xs">View</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="text-[11px] text-[#8A8780]">
          Webhook URL: <span className="font-mono">{(process.env.REACT_APP_BACKEND_URL || "")}/api/emails/webhook</span> ·
          Add this in your Resend dashboard (Webhooks → Add endpoint) and paste the signing secret into <code>RESEND_WEBHOOK_SECRET</code>.
        </div>
      </div>

      {openRow && <DetailPane row={openRow} onClose={() => setOpenRow(null)} />}
    </AppShell>
  );
}

function Kpi({ label, value, tone = "grey" }) {
  const toneCls = {
    green: "bg-[#D4E0D1] text-[#1A3317]",
    amber: "bg-[#F9E4B7] text-[#7A5A11]",
    red:   "bg-[#F4C7B9] text-[#7A2E18]",
    grey:  "bg-[#E5E2D9] text-[#5C5A55]",
  }[tone];
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md p-3" data-testid={`email-kpi-${label.toLowerCase()}`}>
      <div className={`inline-block px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] rounded ${toneCls}`}>{label}</div>
      <div className="font-heading text-2xl mt-1.5 text-[#1A1A1A]">{value}</div>
    </div>
  );
}

function DetailPane({ row, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-xl bg-white h-full overflow-auto" onClick={e => e.stopPropagation()}
           data-testid="email-detail">
        <div className="p-5 border-b border-[#E5E2D9] flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">{KIND_LABEL[row.template_kind] || row.template_kind}</div>
            <div className="font-heading text-lg mt-0.5">{row.subject}</div>
            <div className="text-xs text-[#5C5A55] mt-1">To: {(row.to || []).join(", ")}</div>
            <div className="text-[10px] font-mono text-[#5C5A55] mt-1 break-all">message_id: {row.message_id || "—"}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={14}/></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <KV k="Status" v={row.status} />
            <KV k="From" v={`${row.from_name} <${row.from_email}>`} />
            <KV k="Created" v={(row.created_at || "").replace("T", " ").slice(0, 19)} />
            <KV k="Updated" v={(row.updated_at || "").replace("T", " ").slice(0, 19)} />
            <KV k="Attachments" v={row.attachment_count || 0} />
            <KV k="Tenant" v={row.tenant_id || "—"} />
            {row.related_id && <KV k={`Related ${row.related_kind || "id"}`} v={row.related_id} mono />}
            {row.actor_name && <KV k="Sent by" v={row.actor_name} />}
            {row.first_opened_at && <KV k="First opened" v={row.first_opened_at.replace("T", " ").slice(0, 19)} />}
            {row.first_clicked_at && <KV k="First clicked" v={row.first_clicked_at.replace("T", " ").slice(0, 19)} />}
          </div>
          {row.error && (
            <div className="bg-[#F4C7B9]/40 border border-[#F4C7B9] rounded-md p-3 text-xs text-[#7A2E18]">
              <div className="font-medium mb-0.5">Error</div>{row.error}
            </div>
          )}
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">Events</div>
            <div className="space-y-1.5">
              {(row.events || []).length === 0 && <div className="text-xs text-[#8A8780] italic">No events yet</div>}
              {(row.events || []).map((ev, i) => (
                <div key={i} className="flex items-start gap-2 text-xs border border-[#E5E2D9] rounded-md p-2">
                  <span className="font-medium text-[#1A1A1A]">{ev.event}</span>
                  <span className="text-[#5C5A55] ml-auto">{(ev.at || "").replace("T", " ").slice(0, 19)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const KV = ({ k, v, mono }) => (
  <div>
    <div className="text-[9px] uppercase tracking-[0.18em] text-[#8A8780]">{k}</div>
    <div className={`text-[#2D2B26] ${mono ? "font-mono break-all text-[11px]" : ""}`}>{String(v)}</div>
  </div>
);
